import { useEffect, useRef, useState } from "react";
import { useResource, formatDateTime } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Modal } from "../components/Modal";
import { InitialsAvatar } from "../components/InitialsAvatar";
import type { ChatChannel, ChatMessage } from "../lib/types";

const POLL_MS = 10_000;

export const Chat = () => {
    const { user } = useAuth();
    const canManage = user?.role === "admin" || user?.role === "manager";
    const channels = useResource<Array<ChatChannel & { is_direct?: boolean }>>("/chat-channels?is_archived=false");
    const dms = useResource<Array<{ id: string; partner_name: string }>>("/chat-dms");
    const unread = useResource<Array<{ channel_id: string; unread: number }>>("/chat-unread");

    const [active, setActive] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);
    const [composing, setComposing] = useState(false);
    const directory = useResource<Array<{ id: string; full_name: string }>>(composing ? "/chat-directory" : null);
    const [newName, setNewName] = useState("");
    const [newDesc, setNewDesc] = useState("");
    const [createBusy, setCreateBusy] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    const groups = (channels.data ?? []).filter((c) => !c.is_direct);
    const channelId = active ?? groups[0]?.id ?? null;
    const unreadOf = (id: string) => (unread.data ?? []).find((u) => u.channel_id === id)?.unread ?? 0;
    // Author display name is resolved server-side (user_display_name), so this
    // is all the page needs — no separate employee lookup.
    const messages = useResource<ChatMessage[]>(
        channelId ? `/chat-messages?channel_id=${channelId}` : null
    );

    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const bottomRef = useRef<HTMLDivElement>(null);
    const reload = messages.reload;

    // Simple polling keeps the feed near-live without new infrastructure.
    useEffect(() => {
        if (!channelId) return;
        const timer = setInterval(() => reload(), POLL_MS);
        return () => clearInterval(timer);
    }, [channelId, reload]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages.data?.length]);

    // Opening a channel (or receiving new messages while it's open) moves the
    // read cursor, which clears its unread badge.
    const unreadReload = unread.reload;
    useEffect(() => {
        if (!channelId || messages.data == null) return;
        void api.post(`/chat-channels/${channelId}/read`, {}).then(() => unreadReload());
    }, [channelId, messages.data, unreadReload]);

    const send = async () => {
        if (!channelId || !draft.trim()) return;
        setSending(true);
        try {
            await api.post("/chat-messages", { channel_id: channelId, body: draft.trim() });
            setDraft("");
            reload();
        } finally {
            setSending(false);
        }
    };

    const createChannel = async () => {
        if (!newName.trim()) { setCreateError("A channel name is required."); return; }
        setCreateBusy(true); setCreateError(null);
        try {
            const created = await api.post<{ id: string }>("/chat-channels", {
                name: newName.trim(), description: newDesc.trim() || null,
            });
            setCreating(false); setNewName(""); setNewDesc("");
            channels.reload();
            setActive(created.id);
        } catch (err) {
            setCreateError(err instanceof Error ? err.message : "Could not create the channel");
        } finally {
            setCreateBusy(false);
        }
    };

    return (
        <div className="chat-layout">
            <aside className="chat-channels">
                {canManage && (
                    <button className="btn ghost small" style={{ margin: "0 0 0.4rem" }}
                        onClick={() => setCreating(true)}>＋ New channel</button>
                )}
                {groups.map((c) => {
                    const n = unreadOf(c.id);
                    return (
                        <button
                            key={c.id}
                            className={c.id === channelId ? "channel active" : "channel"}
                            onClick={() => setActive(c.id)}
                        >
                            <b># {c.name}{n > 0 && <span className="chat-badge">{n}</span>}</b>
                            {c.description && <span className="sub">{c.description}</span>}
                        </button>
                    );
                })}
                {!groups.length && <p className="empty">No channels yet.</p>}

                <div className="chat-dm-head">
                    <span>Direct messages</span>
                    <button className="btn ghost small" onClick={() => setComposing(true)}>＋ New</button>
                </div>
                {(dms.data ?? []).map((d) => {
                    const n = unreadOf(d.id);
                    return (
                        <button
                            key={d.id}
                            className={d.id === channelId ? "channel active" : "channel"}
                            onClick={() => setActive(d.id)}
                        >
                            <b>{d.partner_name}{n > 0 && <span className="chat-badge">{n}</span>}</b>
                        </button>
                    );
                })}
                {!dms.data?.length && <p className="muted" style={{ fontSize: "0.75rem", padding: "0 0.3rem" }}>No conversations yet.</p>}
            </aside>

            {composing && (
                <Modal title="New message" subtitle="Pick a person — the conversation is private between the two of you."
                    onClose={() => setComposing(false)}>
                    {(directory.data ?? []).map((p) => (
                        <button key={p.id} className="channel" style={{ width: "100%", textAlign: "left" }}
                            onClick={async () => {
                                const dm = await api.post<{ id: string }>("/chat-dm", { user_id: p.id });
                                setComposing(false);
                                dms.reload();
                                setActive(dm.id);
                            }}>
                            <b>{p.full_name}</b>
                        </button>
                    ))}
                    {!directory.data?.length && <p className="empty">Loading people…</p>}
                </Modal>
            )}

            {creating && (
                <Modal
                    title="New channel"
                    subtitle="A channel is a group — every member of the team sees and can post in it. Use one per team, site or topic."
                    onClose={() => setCreating(false)}
                    footer={
                        <>
                            <button className="btn" disabled={createBusy} onClick={() => void createChannel()}>
                                {createBusy ? "Creating…" : "Create channel"}
                            </button>
                            <button className="btn ghost" disabled={createBusy} onClick={() => setCreating(false)}>Cancel</button>
                        </>
                    }
                >
                    {createError && <div className="error-banner">{createError}</div>}
                    <div className="field">
                        <label>Channel name <span className="req">*</span></label>
                        <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Night shift team" />
                    </div>
                    <div className="field">
                        <label>Description</label>
                        <input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="What this channel is for" />
                    </div>
                </Modal>
            )}

            <section className="chat-pane">
                <div className="chat-messages">
                    {(messages.data ?? []).map((m) => {
                        const mine = m.author === user?.id;
                        return (
                            <div key={m.id} className={mine ? "msg mine" : "msg"}>
                                <InitialsAvatar name={m.author_name} />
                                <div className="msg-body">
                                    <span className="sub">{m.author_name} · {formatDateTime(m.created_at)}</span>
                                    <p>{m.body}</p>
                                </div>
                            </div>
                        );
                    })}
                    {channelId && !messages.data?.length && <p className="empty">No messages yet — say hello.</p>}
                    <div ref={bottomRef} />
                </div>

                <footer className="chat-composer">
                    <textarea
                        rows={2}
                        value={draft}
                        placeholder="Write a message…  (Enter to send, Shift+Enter for a new line)"
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                void send();
                            }
                        }}
                    />
                    <button className="btn" disabled={sending || !draft.trim()} onClick={() => void send()}>
                        Send
                    </button>
                </footer>
            </section>
        </div>
    );
};
